import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, Smartphone, Mail, KeyRound, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { otpErr } from "@/lib/msg91";

const WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID;
const TOKEN_AUTH = process.env.REACT_APP_MSG91_TOKEN_AUTH;

function loadMsg91() {
  return new Promise((resolve, reject) => {
    if (window.initSendOTP) return resolve();
    const s = document.createElement("script");
    s.src = "https://verify.msg91.com/otp-provider.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("MSG91 widget load nahi hua"));
    document.head.appendChild(s);
  });
}
const extractToken = (d) =>
  (typeof d === "string" && d) || d?.message || d?.accessToken || d?.["access-token"] ||
  d?.token || d?.data?.accessToken || d?.data?.["access-token"] || d?.data?.message || null;

// Triple-layer step-up: PIN -> email+password -> OTP (mobile via MSG91, else email) -> /admin
export default function AdminUnlock({ open, onClose }) {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [cid, setCid] = useState("");
  const [pin, setPin] = useState("");
  const [cred, setCred] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [mode, setMode] = useState("email");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [maskedMobile, setMaskedMobile] = useState("");
  const [fullMobile, setFullMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  const msg91Init = useRef(false);
  const submittedRef = useRef(false);

  const reset = () => {
    setStep(1); setCid(""); setPin(""); setCred({ email: "", password: "" }); setOtp("");
    setMode("email"); setMaskedEmail(""); setMaskedMobile(""); setFullMobile(""); setOtpSent(false);
    setNewPin("");
    submittedRef.current = false;
  };
  const close = () => { reset(); onClose(); };

  const handleMobileToken = async (token) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    try {
      const { data } = await api.post("/admin/stepup/verify-mobile", { challenge_id: cid, access_token: token });
      if (data?.stage === "change_pin") { setCid(data.challenge_id || cid); toast.success("Verified — ab naya PIN set karein"); setStep(4); }
      else if (data?.must_change_pin) { toast.success("Unlocked — ab naya PIN set karein"); setStep(4); }
      else { toast.success("Admin panel unlocked!"); close(); nav("/admin"); }
    } catch (er) { submittedRef.current = false; toast.error(apiErr(er)); }
    finally { setBusy(false); }
  };

  const initMobileWidget = async () => {
    await loadMsg91();
    if (msg91Init.current) return;
    msg91Init.current = true;
    window.initSendOTP({
      widgetId: WIDGET_ID, tokenAuth: TOKEN_AUTH, exposeMethods: true,
      success: (d) => { const t = extractToken(d); if (t) handleMobileToken(t); },
      failure: (e) => { setBusy(false); console.error("MSG91 fail", e); if (e) toast.error(`OTP fail: ${otpErr(e)}`); },
    });
  };

  const submitPin = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/admin/stepup/pin", { pin }); setCid(data.challenge_id); setStep(2); }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const submitLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/stepup/login", { challenge_id: cid, ...cred });
      const m = data.mode || "email";
      setMode(m);
      if (m === "mobile") {
        setMaskedMobile(data.otp_mobile_masked || ""); setFullMobile(data.otp_mobile || "");
        try { await initMobileWidget(); } catch { /* widget loads on Send */ }
      } else {
        setMaskedEmail(data.otp_email_masked || ""); toast.success("OTP email par bhej diya");
      }
      setStep(3);
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const sendMobileOtp = async () => {
    setBusy(true); submittedRef.current = false;
    try {
      await initMobileWidget();
      window.sendOtp(fullMobile,
        () => { setOtpSent(true); setBusy(false); toast.success("OTP aapke mobile par bhej diya"); },
        (e) => { setBusy(false); console.error("MSG91 sendOtp fail", e); toast.error(`OTP nahi gaya: ${otpErr(e)}`); });
    } catch { setBusy(false); toast.error("Widget load nahi hua"); }
  };

  const verifyMobileOtp = (e) => {
    e.preventDefault(); setBusy(true); submittedRef.current = false;
    try {
      window.verifyOtp(otp,
        (d) => { const t = extractToken(d); if (t) handleMobileToken(t); },
        (er) => { setBusy(false); console.error("MSG91 verifyOtp fail", er); toast.error(`OTP verify fail: ${otpErr(er)}`); });
    } catch { setBusy(false); toast.error("Verify nahi ho paya"); }
  };

  const submitEmailOtp = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/admin/stepup/verify-otp", { challenge_id: cid, code: otp });
      if (data?.stage === "change_pin") { setCid(data.challenge_id || cid); toast.success("Verified — ab naya PIN set karein"); setStep(4); }
      else if (data?.must_change_pin) { toast.success("Unlocked — ab naya PIN set karein"); setStep(4); }
      else { toast.success("Admin panel unlocked!"); close(); nav("/admin"); } }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  const submitNewPin = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(newPin) || newPin === "009838") { toast.error("Naya PIN 4-8 digit ka ho aur default (009838) nahi"); return; }
    setBusy(true);
    try { await api.post("/admin/stepup/change-pin", { challenge_id: cid, pin: newPin }); toast.success("Naya PIN set ho gaya!"); close(); nav("/admin"); }
    catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  if (!open) return null;
  const Dot = ({ n }) => <div className={`w-2.5 h-2.5 rounded-full ${step >= n ? "bg-emerald-500" : "bg-slate-300"}`} />;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-unlock-modal" onClick={step === 4 ? undefined : close}>
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-7 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        {step !== 4 && <button onClick={close} data-testid="admin-unlock-close" className="absolute right-5 top-5 text-slate-400 hover:text-slate-700"><X size={18} /></button>}
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-900 flex items-center justify-center text-white"><ShieldCheck size={26} /></div>
          <h2 className="text-lg font-extrabold mt-3">Rs. MART POS — Owner Access</h2>
          <p className="text-xs text-slate-500">3-layer secure unlock</p>
          <div className="flex items-center gap-2 mt-3"><Dot n={1} /><Dot n={2} /><Dot n={3} /></div>
        </div>

        {step === 1 && (
          <form onSubmit={submitPin} className="mt-6 space-y-3">
            <label className="text-xs font-bold text-slate-500 flex items-center gap-1"><Lock size={13} /> Step 1 — Security PIN</label>
            <input data-testid="unlock-pin" type="password" inputMode="numeric" autoFocus value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Enter PIN" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm tracking-widest text-center" />
            <button data-testid="unlock-pin-submit" disabled={busy || !pin} className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl font-bold">{busy ? "…" : "Continue"}</button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitLogin} className="mt-6 space-y-3">
            <label className="text-xs font-bold text-slate-500 flex items-center gap-1"><KeyRound size={13} /> Step 2 — Owner Credentials</label>
            <input data-testid="unlock-email" type="email" autoFocus value={cred.email} onChange={(e) => setCred({ ...cred, email: e.target.value })} placeholder="Owner email" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" />
            <input data-testid="unlock-password" type="password" value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="Password" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" />
            <button data-testid="unlock-login-submit" disabled={busy} className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl font-bold">{busy ? "…" : "Continue"}</button>
          </form>
        )}

        {step === 3 && mode === "mobile" && (
          <form onSubmit={verifyMobileOtp} className="mt-6 space-y-3">
            <label className="text-xs font-bold text-slate-500 flex items-center gap-1"><Smartphone size={13} /> Step 3 — Mobile OTP</label>
            <p className="text-[11px] text-slate-500">OTP aayega mobile par: <b>+{maskedMobile}</b></p>
            {!otpSent ? (
              <button type="button" data-testid="unlock-send-mobile-otp" onClick={sendMobileOtp} disabled={busy} className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl font-bold">{busy ? "…" : "Send OTP to my mobile"}</button>
            ) : (
              <>
                <input data-testid="unlock-mobile-otp" inputMode="numeric" autoFocus value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="OTP code" maxLength={8} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm tracking-[0.4em] text-center" />
                <button data-testid="unlock-mobile-otp-submit" disabled={busy || otp.length < 4} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-bold">{busy ? "…" : "Unlock Admin Panel"}</button>
                <button type="button" data-testid="unlock-mobile-otp-resend" onClick={sendMobileOtp} disabled={busy} className="w-full text-[11px] text-slate-500 hover:text-slate-700">OTP dobara bhejein</button>
              </>
            )}
          </form>
        )}

        {step === 3 && mode !== "mobile" && (
          <form onSubmit={submitEmailOtp} className="mt-6 space-y-3">
            <label className="text-xs font-bold text-slate-500 flex items-center gap-1"><Mail size={13} /> Step 3 — Email OTP</label>
            <p className="text-[11px] text-slate-500">OTP bheja gaya: <b>{maskedEmail}</b></p>
            <input data-testid="unlock-otp" inputMode="numeric" autoFocus value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" maxLength={6} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm tracking-[0.5em] text-center" />
            <button data-testid="unlock-otp-submit" disabled={busy || otp.length < 6} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-bold">{busy ? "…" : "Unlock Admin Panel"}</button>
          </form>
        )}
        {step === 4 && (
          <form onSubmit={submitNewPin} className="mt-6 space-y-3" data-testid="unlock-newpin-form">
            <label className="text-xs font-bold text-amber-600 flex items-center gap-1"><Lock size={13} /> Default PIN detected — naya PIN set karein</label>
            <p className="text-[11px] text-slate-500">Security ke liye apna default PIN badalna zaroori hai. Ye ek baar ka step hai.</p>
            <input data-testid="unlock-newpin" type="password" inputMode="numeric" autoFocus value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} placeholder="Naya 4-8 digit PIN" maxLength={8} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm tracking-widest text-center" />
            <button data-testid="unlock-newpin-submit" disabled={busy || newPin.length < 4} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-bold">{busy ? "…" : "Set PIN & Enter"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
